// UI utilities, global state, and small helpers
export const APP_VERSION = 'V1.0.3';
const rawConfig = window.__APP_CONFIG__ || {};
const isTestFlag = typeof rawConfig.MEEST_PUDO_USE_TEST === 'string'
  ? rawConfig.MEEST_PUDO_USE_TEST.toLowerCase() === 'true'
  : Boolean(rawConfig.MEEST_PUDO_USE_TEST);

export const CONFIG = Object.freeze({
  isTestPudo: isTestFlag,
});
export const tg = window.Telegram?.WebApp;
if (tg) {
  try {
    // Ensure Telegram client marks the webapp ready before invoking advanced APIs (QR scan, etc.)
    window.Telegram?.WebApp?.ready?.();
  } catch (_) {
    // ignore readiness errors – fall back to default behavior
  }
  tg.expand?.();
}

const inlineAlertHost = document.getElementById('inline_alert_host');

export const DEFAULT_SETTINGS = Object.freeze({
  theme: 'dark',
  tsdMode: false,
  autoOpenCell: false,
  trainingMode: false,
  version: 1,
});

export const state = {
  branchRef: null,
  branchID: null,
  branchIsOpen: true,
  courier: null,
  pickup: [],
  delivery: [],
  skippedDelivery: new Set(),
  deliveryFilterProblemOnly: false,
  deliveryProblemTypes: {}, // parcelID -> problemType
  // pickup problem tracking
  pickupFilterProblemOnly: false,
  pickupProblemTypes: {}, // parcelID -> problemType (pickup flow)
  // total count snapshot for delivery flow to show progress chips
  deliveryTotal: 0,
  // postamat info snapshot (from TelegramBot_CourierParcels)
  postamatInfo: null,
  // Мережа точки з /locker/by-qr (meest / epicentr / rozetka / unknown).
  branchNetworkId: null,
  // Звʼязок поштомата з 1С (postamat-online.js → normalizeOnlineStatus) або null.
  postamatOnline: null,
  // «Спершу посилки Епіцентру» (locker-ep-first.js): опрацьовані посилки за
  // візит — Map(branchRef → { inserted, problem }). Не скидається новим
  // скануванням того ж поштомата: 1С ще віддає вкладені й «проблемні».
  // Переживає перезавантаження сторінки: localStorage до кінця доби + те, що
  // знає сервер (/locker/parcels), — див. epFirstVisits() в app.js.
  epFirstVisits: null,
  processedPickup: new Set(),
  skippedPickup: new Set(),
  currentPickup: null,
  currentPickupTrans: null,
  currentDelivery: null,
  currentDeliveryTrans: null,
  currentCellSize: 'S',
  currentDeliveryAttempts: 0,
  autoScanDone: false,
  deliveryIndex: {},
  pickupIndex: {},
  loading: false,
  // whether user is in a locker hardware interaction flow (don’t interrupt with modals)
  operationActive: false,
  // maintenance overlay state
  maintenance: {
    visible: false,
    message: null,
    endsAt: null,
    _timer: null,
    _closingCheckRunning: false,
    pending: null, // { message, endsAt } when suppressed during operation
  },
  errors: {
    backendDownVisible: false,
    upstreamErrorVisible: false,
  },
  // demo mode flag
  demoMode: false,
  // stack для undo проблем по delivery
  _deliveryProblemStack: [],
  problemCountBase: 0,
  // inventory flow
  inventoryTasks: [],
  inventoryBranch: null,
  inventorySelected: null,
  inventoryLoading: false,
  lastScreenBeforeInventory: null,
  isTestPudo: CONFIG.isTestPudo,
  settings: { ...DEFAULT_SETTINGS },
  problemTemplates: {
    locker: [],
    pickup: [],
    delivery: [],
  },
  pendingPickupProblem: null,
  pendingDeliveryProblem: null,
};

const logEl = document.getElementById('log');
export function log(...msgs) {
  if (!logEl) return;
  const ts = new Date().toLocaleTimeString();
  const parts = msgs.map((m) => {
    if (m == null) return String(m);
    if (typeof m === 'string') return m;
    try {
      return JSON.stringify(m);
    } catch (_) {
      return String(m);
    }
  });
  logEl.textContent += `[${ts}] ${parts.join(' ')}\n`;
  logEl.scrollTop = logEl.scrollHeight;
}

// Diagnostic banner (informational, above lists)
export function showDiagBanner(message) {
  const els = document.querySelectorAll('.diag-banner');
  els.forEach((el) => {
    el.textContent = String(message || 'Діагностика');
    el.classList.remove('hidden');
  });
}
export function hideDiagBanner() {
  const els = document.querySelectorAll('.diag-banner');
  els.forEach((el) => el.classList.add('hidden'));
}

/** Куди вставити плашку: id або сам елемент; null — місця на сторінці немає. */
function resolveInlineAlertHost(host) {
  if (!host) return null;
  if (typeof host === 'string') return document.getElementById(host);
  return host && host.nodeType === 1 ? host : null;
}

/**
 * @param {{title?: string, message?: string, tone?: string, actions?: Array,
 *          id?: string, host?: string|HTMLElement, compact?: boolean,
 *          onClose?: Function}} opts
 *   id — ставиться на контейнер, щоб інші частини застосунку могли
 *     перевірити, чи попередження ще на екрані (напр. блокер звʼязку не
 *     перекриває критичне «комірка могла відкритись»).
 *   host — явне місце (id або елемент). Без нього — загальний
 *     #inline_alert_host над шапкою, як і раніше; якщо вказаного місця на
 *     сторінці немає — теж він.
 *   compact — щільна верстка: менші відступи, кнопки дій в один ряд.
 *   actions[].className — додатковий клас кнопки (щоб її можна було виділити).
 *   onClose — коли плашку закрив курʼєр (хрестик або кнопка дії); не
 *     викликається, якщо її прибрав код (remove, заміна за тим самим id).
 */
export function createInlineAlert({
  title, message, tone = 'info', actions = [], id = '', host = null, compact = false, onClose = null,
} = {}) {
  const target = resolveInlineAlertHost(host) || inlineAlertHost;
  if (!target) {
    try {
      window.alert([title, message].filter(Boolean).join('\n'));
    } catch (_) {}
    return () => {};
  }

  const container = document.createElement('div');
  container.className = `inline-alert inline-alert-${tone}${compact ? ' inline-alert-compact' : ''}`;
  if (id) {
    // Попереднє попередження з тим самим id прибираємо одразу: два однакові
    // блоки поспіль кур'єр читає як дві різні аварії.
    document.getElementById(id)?.remove();
    container.id = String(id);
  }

  if (title) {
    const heading = document.createElement('div');
    heading.className = 'inline-alert-title';
    heading.textContent = String(title);
    container.appendChild(heading);
  }

  if (message) {
    const body = document.createElement('div');
    body.className = 'inline-alert-message';
    body.textContent = String(message);
    container.appendChild(body);
  }

  if (actions && Array.isArray(actions) && actions.length) {
    const actionsWrap = document.createElement('div');
    actionsWrap.className = 'inline-alert-actions';
    actions.forEach((action) => {
      if (!action || !action.label) return;
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = action.className ? `inline-alert-btn ${action.className}` : 'inline-alert-btn';
      btn.textContent = String(action.label);
      if (typeof action.onClick === 'function') {
        btn.addEventListener('click', (evt) => {
          try {
            action.onClick(evt);
          } catch (err) {
            console.error(err);
          } finally {
            closeAlert();
          }
        });
      }
      actionsWrap.appendChild(btn);
    });
    container.appendChild(actionsWrap);
  }

  const closeBtn = document.createElement('button');
  closeBtn.type = 'button';
  closeBtn.className = 'inline-alert-close';
  closeBtn.setAttribute('aria-label', 'Закрити');
  closeBtn.textContent = '✕';
  closeBtn.addEventListener('click', () => closeAlert());
  container.appendChild(closeBtn);

  target.appendChild(container);

  let closed = false;
  function closeAlert() {
    container.classList.add('inline-alert-hide');
    setTimeout(() => {
      container.remove();
    }, 180);
    if (closed) return;
    closed = true;
    if (typeof onClose === 'function') {
      try {
        onClose();
      } catch (err) {
        console.error(err);
      }
    }
  }

  return closeAlert;
}

// Loader overlay
let loaderCount = 0;
let loaderTimer = null;
let loaderSafetyTimer = null;
const LOADER_SAFETY_TIMEOUT = 60000; // 60s max loader display

function clearLoaderSafetyTimer() {
  if (loaderSafetyTimer) { clearTimeout(loaderSafetyTimer); loaderSafetyTimer = null; }
}

export function showLoader(text, { immediate = false } = {}) {
  const el = document.getElementById('loader');
  const tx = document.getElementById('loader_text');
  if (tx) tx.textContent = text ? String(text) : 'Завантаження…';
  loaderCount++;
  // Safety: cap runaway counter to prevent permanent loader
  if (loaderCount > 10) {
    console.warn('[loader] counter overflow, resetting to 1');
    loaderCount = 1;
  }
  if (loaderTimer) {
    clearTimeout(loaderTimer);
    loaderTimer = null;
  }
  // Safety timer: auto-hide after 60s to prevent stuck loader
  clearLoaderSafetyTimer();
  loaderSafetyTimer = setTimeout(() => {
    if (loaderCount > 0) {
      console.warn('[loader] safety timeout reached, forcing hide');
      loaderCount = 0;
      el?.classList.remove('visible');
    }
  }, LOADER_SAFETY_TIMEOUT);
  if (immediate) {
    el?.classList.add('visible');
  } else {
    loaderTimer = setTimeout(() => {
      el?.classList.add('visible');
      loaderTimer = null;
    }, 150);
  }
}
/** Update loader text without changing the counter / visibility state. */
export function updateLoaderText(text) {
  const tx = document.getElementById('loader_text');
  if (tx && text) tx.textContent = String(text);
}
export function hideLoader() {
  const el = document.getElementById('loader');
  if (loaderCount > 0) loaderCount--;
  if (loaderCount === 0) {
    clearLoaderSafetyTimer();
    if (loaderTimer) {
      clearTimeout(loaderTimer);
      loaderTimer = null;
    }
    el?.classList.remove('visible');
  }
}
/** Force-reset loader state. Use as emergency escape hatch. */
export function resetLoader() {
  loaderCount = 0;
  clearLoaderSafetyTimer();
  if (loaderTimer) { clearTimeout(loaderTimer); loaderTimer = null; }
  document.getElementById('loader')?.classList.remove('visible');
}

// Maintenance overlay
function fmtDuration(ms) {
  if (ms <= 0) return '00:00';
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  const pad = (n) => String(n).padStart(2, '0');
  if (h > 0) return `${pad(h)}:${pad(m)}:${pad(s)}`;
  return `${pad(m)}:${pad(s)}`;
}

async function fetchMaintenanceStatusOnce() {
  try {
    const r = await fetch('/webapp/maintenance/status', { credentials: 'include' });
    if (!r.ok) return { active: false };
    return await r.json();
  } catch (_) {
    return { active: false };
  }
}

export function setOperationActive(active) {
  state.operationActive = !!active;
  document.body.classList.toggle('operation-active', !!active);
  if (typeof window !== 'undefined' && window.__maintenance && typeof window.__maintenance.setOperationActive === 'function') {
    window.__maintenance.setOperationActive(!!active);
  }
  // If leaving operation and maintenance was pending, try to show it now
  if (!active && state.maintenance.pending) {
    const { message, endsAt } = state.maintenance.pending;
    state.maintenance.pending = null;
    showMaintenanceOverlay(message, endsAt);
  }
}

export function showMaintenanceOverlay(message, endsAt) {
  // Delegate to standalone maintenance module if loaded
  if (typeof window !== 'undefined' && window.__maintenance) {
    state.maintenance.visible = true;
    state.maintenance.message = message;
    state.maintenance.endsAt = endsAt || null;
    window.__maintenance.show(message, endsAt);
    return;
  }
  // Legacy fallback (kept for safety if maintenance.js not loaded)
  if (state.operationActive) {
    state.maintenance.pending = { message, endsAt };
    return;
  }
  const box = document.getElementById('maintenance');
  const txt = document.getElementById('maintenance_text');
  const cd = document.getElementById('maintenance_countdown');
  state.maintenance.visible = true;
  state.maintenance.message = message || 'Тимчасове обслуговування';
  state.maintenance.endsAt = endsAt || null;
  if (txt) txt.textContent = String(state.maintenance.message);
  box?.classList.add('visible');
}

export function hideMaintenanceOverlay() {
  if (typeof window !== 'undefined' && window.__maintenance) {
    state.maintenance.visible = false;
    state.maintenance.message = null;
    state.maintenance.endsAt = null;
    window.__maintenance.hide();
    return;
  }
  const box = document.getElementById('maintenance');
  state.maintenance.visible = false;
  state.maintenance.message = null;
  state.maintenance.endsAt = null;
  if (state.maintenance._timer) {
    clearInterval(state.maintenance._timer);
    state.maintenance._timer = null;
  }
  state.maintenance._closingCheckRunning = false;
  state.maintenance.pending = null;
  box?.classList.remove('visible');
}

// Backend down overlay
//
// ЗАПАСНИЙ ШЛЯХ. У нормі цим оверлеєм керує connectivity.js: він сам робить
// кілька спроб, сам ховає екран, щойно сервер ожив, і сам вішає обробник на
// кнопку. Сюди потрапляємо лише тоді, коли модуль не підвантажився (окремий
// запит, на поганій мережі падає саме тоді, коли найпотрібніший).
//
// Тому тут ОБОВʼЯЗКОВО: (1) чесний текст — ніхто нічого не перевіряє у фоні,
// і обіцяти «розблокується сам» не можна; (2) робоча кнопка — інакше кур'єр
// замкнений у застосунку перед екраном, який не зникає.
export function showBackendDownOverlay() {
  state.errors.backendDownVisible = true;
  const box = document.getElementById('backend_down');
  if (!box) return;
  const title = document.getElementById('backend_down_title');
  const text = document.getElementById('backend_down_text');
  const note = document.getElementById('backend_down_note');
  if (title) title.textContent = 'Бекенд недоступний';
  if (text) text.textContent = 'Неможливо зʼєднатися з сервером. Перевірте мережу або спробуйте пізніше.';
  if (note) note.textContent = '';
  const btn = document.getElementById('btn_retry_backend');
  if (btn && !btn.dataset.fallbackWired) {
    btn.dataset.fallbackWired = '1';
    btn.addEventListener('click', () => location.reload());
  }
  box.classList.remove('hidden');
  box.classList.add('visible');
}
export function hideBackendDownOverlay() {
  state.errors.backendDownVisible = false;
  document.getElementById('backend_down')?.classList.remove('visible');
}

// Upstream (Meest) error overlay
export function showUpstreamErrorOverlay(message) {
  const el = document.getElementById('upstream_error');
  const txt = document.getElementById('upstream_error_text');
  if (txt) txt.textContent = String(message || 'Помилка сервісу Meest');
  state.errors.upstreamErrorVisible = true;
  el?.classList.add('visible');
}
export function hideUpstreamErrorOverlay() {
  state.errors.upstreamErrorVisible = false;
  document.getElementById('upstream_error')?.classList.remove('visible');
}

// Identity forbidden overlay (no courier identity)
export function showIdentityForbiddenOverlay(message) {
  const el = document.getElementById('identity_forbidden');
  const txt = document.getElementById('identity_forbidden_text');
  if (txt && message) txt.textContent = String(message);
  el?.classList.add('visible');
}

// Panels
export function show(id) {
  document.querySelectorAll('.panel').forEach((p) => p.classList.add('hidden'));
  const el = document.getElementById(id);
  if (el) el.classList.remove('hidden');
  try {
    if (id && id !== 'screen_start') localStorage.setItem('lastScreen', id);
  } catch (_) {}
  // Toggle demo button visibility: only on start screen
  const demoBtn = document.getElementById('btn_demo');
  if (demoBtn) demoBtn.style.display = id === 'screen_start' ? '' : 'none';
}
/**
 * Build a return-URL string based on the current page's `returnTo` query param.
 * Returns null when no recognised returnTo value is present. Extracted from
 * finish() so the routing logic is unit-testable without invoking redirects.
 * - returnTo=delivery2  → /branches.html (delivery2.html was retired)
 * - returnTo=branches   → /branches.html (passing tg_user_id and optional courier_meest_id)
 */
export function getReturnUrl() {
  const params = new URLSearchParams(window.location.search);
  const returnTo = params.get('returnTo');
  if (!returnTo) return null;
  const tgUserId = params.get('tg_user_id') || '';
  if (returnTo === 'delivery2') {
    return `/branches.html?tg_user_id=${encodeURIComponent(tgUserId)}`;
  }
  if (returnTo === 'branches') {
    let url = `/branches.html?tg_user_id=${encodeURIComponent(tgUserId)}`;
    const courierMeestId = params.get('courier_meest_id') || '';
    if (courierMeestId) url += `&courier_meest_id=${encodeURIComponent(courierMeestId)}`;
    return url;
  }
  return null;
}

export function finish() {
  show('screen_done');
  if (state.demoMode) return;

  const returnUrl = getReturnUrl();
  if (returnUrl) {
    setTimeout(() => { window.location.href = returnUrl; }, 800);
    return;
  }

  setTimeout(() => {
    try {
      tg?.close?.();
    } catch (_) {}
  }, 800);
}
export function setPhase(_) {
  // phase label hidden/no-op
}

// Toast + beep
// ms — скільки тримати на екрані. 1.6 с вистачає на «Готово», але не на
// пояснення з інструкцією: довгий текст зникає, поки курʼєр його читає.
// Тому тексти помилок поштомата просять більший час явно.
export function toast(msg, ms = 1600) {
  const t = document.getElementById('toast');
  if (!t) return;
  t.textContent = String(msg);
  t.style.display = 'block';
  clearTimeout(t._tid);
  t._tid = setTimeout(() => {
    t.style.display = 'none';
  }, Number(ms) > 0 ? Number(ms) : 1600);
}
// Toast з кнопкою Undo
export function showUndoToast(msg, onUndo) {
  const t = document.getElementById('toast');
  if (!t) return;
  t.innerHTML = '';
  const span = document.createElement('span');
  span.textContent = String(msg);
  const btn = document.createElement('button');
  btn.textContent = 'Undo';
  btn.style.marginLeft = '12px';
  btn.style.background = '#484f58';
  btn.style.border = '1px solid #666';
  btn.style.fontSize = '14px';
  btn.onclick = () => {
    try { onUndo && onUndo(); } catch (_) {}
    t.style.display = 'none';
  };
  t.appendChild(span);
  t.appendChild(btn);
  t.style.display = 'block';
  clearTimeout(t._tid);
  t._tid = setTimeout(() => {
    t.style.display = 'none';
  }, 6000);
}
export function beepShort() {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    const ctx = new AC();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = 'square';
    o.frequency.value = 880;
    o.connect(g);
    g.connect(ctx.destination);
    g.gain.setValueAtTime(0.12, ctx.currentTime);
    o.start();
    setTimeout(() => {
      o.stop();
      ctx.close();
    }, 120);
  } catch (_) {}
}

// Theme handling
const THEME_LS_KEY = 'uiTheme';

export function applyTheme(theme, { persistLocal = true } = {}) {
  const next = theme === 'light' ? 'light' : 'dark';
  document.body.classList.toggle('theme-light', next === 'light');
  state.settings = state.settings || { ...DEFAULT_SETTINGS };
  state.settings.theme = next;
  if (persistLocal) {
    try {
      localStorage.setItem(THEME_LS_KEY, next);
    } catch (_) {}
  }
}

export function initHeaderToggles() {
  // Log toggle via version badge disabled - was confusing for users
  // Keep log element hidden by default
}

// Locker bar helpers
export async function applyLockerCtx(ctx) {
  state.branchRef = ctx.branch.branchIDRef;
  state.branchID = ctx.branch.branchID;
  state.courier = ctx.courier;
  state.branchIsOpen = ctx.branch.isOpen !== false; // Default to true if missing
  
  const fullName = ctx.branch.fullName || ctx.branch.shortName || 'Поштомат';
  const title = document.getElementById('locker_title');
  if (title) title.textContent = fullName;
  
  // Show closed branch warning if applicable
  const closedWarning = document.getElementById('branch_closed_warning');
  if (closedWarning) {
    if (!state.branchIsOpen) {
      closedWarning.classList.remove('hidden');
    } else {
      closedWarning.classList.add('hidden');
    }
  }
  
  document.getElementById('locker_bar')?.classList.remove('hidden');
}
